import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGuildConfig, useGuildPatch } from '../../../lib/auditQueries.js';
import { useRoles } from '../../../lib/automodQueries.js';
import { Breadcrumbs } from '../../../components/Breadcrumbs.js';
import { LanguageSwitcher } from '../../../components/LanguageSwitcher.js';

export const Route = createFileRoute('/guilds/$guildId/auto-role')({
  component: AutoRolePage,
});

interface FormState {
  autoRoleEnabled: boolean;
  autoRoleTrustedRoleId: string | null;
  autoRoleMinDays: number;
  autoRoleMinPoints: number;
  autoRoleRequireMode: 'and' | 'or';
  autoRoleCountMessages: boolean;
  autoRoleCountReactions: boolean;
  autoRoleMaxActiveInfractions: number;
  spamTrustMode: 'trusted_role' | 'any_role' | 'both';
}

function pick(c: NonNullable<ReturnType<typeof useGuildConfig>['data']>): FormState {
  return {
    autoRoleEnabled: c.autoRoleEnabled,
    autoRoleTrustedRoleId: c.autoRoleTrustedRoleId,
    autoRoleMinDays: c.autoRoleMinDays,
    autoRoleMinPoints: c.autoRoleMinPoints,
    autoRoleRequireMode: c.autoRoleRequireMode,
    autoRoleCountMessages: c.autoRoleCountMessages,
    autoRoleCountReactions: c.autoRoleCountReactions,
    autoRoleMaxActiveInfractions: c.autoRoleMaxActiveInfractions,
    spamTrustMode: c.spamTrustMode,
  };
}

function AutoRolePage() {
  const { t } = useTranslation();
  const { guildId } = Route.useParams();
  const config = useGuildConfig(guildId);
  const patch = useGuildPatch(guildId);
  const roles = useRoles(guildId);

  const [state, setState] = useState<FormState | null>(null);

  useEffect(() => {
    if (config.data && !state) setState(pick(config.data));
  }, [config.data, state]);

  if (!state) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100">
        <Header guildId={guildId} />
        <p className="mx-auto max-w-3xl px-6 py-8 text-sm text-zinc-400">{t('common.loading')}</p>
      </div>
    );
  }

  const set = <K extends keyof FormState>(k: K, v: FormState[K]): void => {
    setState((s) => (s ? { ...s, [k]: v } : s));
  };
  const disabled = !state.autoRoleEnabled;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Header guildId={guildId} />
      <main className="mx-auto max-w-3xl px-6 py-8">
        <p className="mb-6 text-sm text-zinc-400">{t('autoRole.subtitle')}</p>

        <div className="space-y-5 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
          <ToggleRow
            label={t('autoRole.toggle.enable')}
            hint={t('autoRole.toggle.enableHint')}
            checked={state.autoRoleEnabled}
            onChange={(v) => {
              set('autoRoleEnabled', v);
            }}
          />

          <Field label={t('autoRole.field.role')} hint={t('autoRole.field.roleHint')}>
            <select
              className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm disabled:opacity-50"
              value={state.autoRoleTrustedRoleId ?? ''}
              disabled={disabled}
              onChange={(e) => {
                set('autoRoleTrustedRoleId', e.target.value || null);
              }}
            >
              <option value="">{t('autoRole.field.rolePlaceholder')}</option>
              {(roles.data ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t('autoRole.field.requireMode')} hint={t('autoRole.field.requireModeHint')}>
            <select
              className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm disabled:opacity-50"
              value={state.autoRoleRequireMode}
              disabled={disabled}
              onChange={(e) => {
                set('autoRoleRequireMode', e.target.value as FormState['autoRoleRequireMode']);
              }}
            >
              <option value="and">{t('autoRole.requireMode.and')}</option>
              <option value="or">{t('autoRole.requireMode.or')}</option>
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label={t('autoRole.field.minDays')}>
              <NumberInput
                value={state.autoRoleMinDays}
                min={0}
                max={365}
                disabled={disabled}
                onChange={(v) => {
                  set('autoRoleMinDays', v);
                }}
              />
            </Field>
            <Field label={t('autoRole.field.minPoints')}>
              <NumberInput
                value={state.autoRoleMinPoints}
                min={0}
                max={100000}
                disabled={disabled}
                onChange={(v) => {
                  set('autoRoleMinPoints', v);
                }}
              />
            </Field>
          </div>

          <Field label={t('autoRole.field.points')} hint={t('autoRole.field.pointsHint')}>
            <div className="flex gap-6">
              <Checkbox
                label={t('autoRole.points.messages')}
                checked={state.autoRoleCountMessages}
                disabled={disabled}
                onChange={(v) => {
                  set('autoRoleCountMessages', v);
                }}
              />
              <Checkbox
                label={t('autoRole.points.reactions')}
                checked={state.autoRoleCountReactions}
                disabled={disabled}
                onChange={(v) => {
                  set('autoRoleCountReactions', v);
                }}
              />
            </div>
          </Field>

          <Field
            label={t('autoRole.field.maxInfractions')}
            hint={t('autoRole.field.maxInfractionsHint')}
          >
            <NumberInput
              value={state.autoRoleMaxActiveInfractions}
              min={0}
              max={100}
              disabled={disabled}
              onChange={(v) => {
                set('autoRoleMaxActiveInfractions', v);
              }}
            />
          </Field>

          <Field label={t('autoRole.field.trustMode')} hint={t('autoRole.field.trustModeHint')}>
            <select
              className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm disabled:opacity-50"
              value={state.spamTrustMode}
              disabled={disabled}
              onChange={(e) => {
                set('spamTrustMode', e.target.value as FormState['spamTrustMode']);
              }}
            >
              <option value="trusted_role">{t('autoRole.trustMode.trustedRole')}</option>
              <option value="any_role">{t('autoRole.trustMode.anyRole')}</option>
              <option value="both">{t('autoRole.trustMode.both')}</option>
            </select>
          </Field>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                if (config.data) setState(pick(config.data));
              }}
              className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-800"
            >
              {t('autoRole.button.reset')}
            </button>
            <button
              type="button"
              disabled={patch.isPending}
              onClick={() => {
                patch.mutate(state);
              }}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {patch.isPending ? t('common.loading') : t('autoRole.button.save')}
            </button>
          </div>
          {patch.isError ? (
            <p className="text-xs text-rose-400">
              {patch.error instanceof Error ? patch.error.message : t('common.error')}
            </p>
          ) : null}
          {patch.isSuccess ? (
            <p className="text-xs text-emerald-400">{t('autoRole.saved')}</p>
          ) : null}
        </div>
      </main>
    </div>
  );
}

function Header({ guildId }: { guildId: string }) {
  const { t } = useTranslation();
  const config = useGuildConfig(guildId);
  return (
    <header className="border-b border-zinc-800">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
        <Link to="/" className="shrink-0">
          <img src="/icon.png" alt="Frankenstein" className="h-6 w-6" />
        </Link>
        <Breadcrumbs
          items={[
            { label: t('app.name'), to: '/' },
            { label: config.data?.name ?? guildId, to: '/guilds/$guildId', params: { guildId } },
            { label: t('autoRole.title') },
          ]}
        />
        <div className="ml-auto">
          <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 rounded border-zinc-700 bg-zinc-900"
        checked={checked}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
      />
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="mt-1 text-xs text-zinc-500">{hint}</p>
      </div>
    </label>
  );
}

function Checkbox({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-zinc-700 bg-zinc-900"
        checked={checked}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
      />
      {label}
    </label>
  );
}

function NumberInput({
  value,
  min,
  max,
  disabled,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  disabled: boolean;
  onChange: (v: number) => void;
}) {
  return (
    <input
      type="number"
      min={min}
      max={max}
      value={value}
      disabled={disabled}
      onChange={(e) => {
        onChange(Math.max(min, Math.min(max, Math.floor(Number(e.target.value) || 0))));
      }}
      className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm tabular-nums disabled:opacity-50"
    />
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs text-zinc-400">{label}</label>
      {children}
      {hint ? <p className="mt-1 text-xs text-zinc-500">{hint}</p> : null}
    </div>
  );
}
